// src/pos-app/surfaces/waiter/SidePanel.js
//
// F12a-N Task W3 — the right panel's real content: Waitlist | Reservations
// tabs, entry cards, Seat / Notify actions and the two add-guest modals
// (design contract: docs/mockups/f12a-n/waiter-tables.html — .ptabs,
// .panel-body, .panel-foot, .wcard, #waitModal, #resModal). Replaces the
// W1 placeholder empty states that lived inline in TablesSurface.
//
// Ownership split (same props-in/callbacks-out pattern as FloorCanvas):
// TablesSurface owns the DATA (waitlist/reservation loading inside its
// existing 30s refreshAll poll, the Upcoming-vs-Past split, seat-mode
// state, service calls, toasts). SidePanel owns only PRESENTATION state:
// the active tab, the two modals' visibility + form fields. Submitting a
// modal awaits the parent callback — the parent performs the
// guestService.create + add/create sequence and returns true/false; the
// modal closes-and-resets only on true, staying open (with the parent's
// error toast visible) on false, exactly like the legacy page which only
// reset its form fields inside the try branch.
//
// Source-range table (legacy TableServicePage.js, current line numbers):
//   timeAgo                <- 69-77   (client-side waiting-time, byte-faithful)
//   formatTime             <- 79-83   (reservation HH:MM, byte-faithful)
//   tabs + count badges    <- 1177-1210 (count only when > 0; reservations
//                             tab counts UPCOMING only, not past)
//   waitlist entry card    <- 1244-1301 (name ellipsis, phone, party,
//                             waiting-time from created_at || added_at,
//                             Seat gated to status waiting|notified)
//   NOTIFIED chip          <- mockup .notified (legacy rendered a generic
//                             statusBadge for waiting|notified; the binding
//                             mockup shows a chip only for notified — the
//                             whole list is already status=waiting,notified)
//   Notify action          <- mockup [data-notify] (no legacy equivalent —
//                             waitingListService.notify existed unused;
//                             parent wires it)
//   reservations sections  <- 1338-1444 (Upcoming full-opacity w/ Seat for
//                             booked|confirmed; Past dimmed, no actions,
//                             status chip)
//   add-to-waitlist modal  <- 1453-1598 (name/phone/party stepper, Enter
//                             submits when name present, disabled submit)
//   add-reservation modal  <- 1600-1787 (name/phone/date/time/party/notes;
//                             date defaults to TODAY local — 1311-1315 —
//                             never toISOString for the picker value)
//
// Modals: scrim + Escape. F12a-N Task W5 centralized Escape-key handling
// into TablesSurface's single keydown authority (see that file's header for
// the full priority chain) — this component no longer runs its own window
// keydown listener. Instead it's wrapped in forwardRef, exposing
// `closeModals()` via useImperativeHandle, and reports open/closed state up
// through the optional `onModalOpenChange(boolean)` prop (fired whenever
// showWaitModal||showResModal flips) so the parent's chain knows whether
// either modal is the topmost overlay. Only one modal can be open at a time
// here, so `closeModals()` simply clears both flags. Tokens only, logical
// properties for RTL, all tap targets ≥44px, safe-area padding on the panel
// foot and modal body.
import { createPortal } from 'react-dom';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const CSS = `
.wsp-root{flex:1;min-height:0;display:flex;flex-direction:column;}

/* ---- tabs (mockup .ptabs) ---- */
.wsp-tabs{flex:none;display:flex;gap:6px;padding:12px 14px 10px;
  border-bottom:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.wsp-tab{flex:1;min-height:44px;border-radius:11px;font-family:inherit;font-size:13px;font-weight:800;
  color:var(--c-muted);display:inline-flex;align-items:center;justify-content:center;gap:8px;
  background:var(--c-faint);border:1px solid var(--c-border);cursor:pointer;
  transition:border-color .12s, color .12s;}
.wsp-tab.active{background:var(--c-greenLight);border-color:var(--c-greenBorder);color:var(--c-green);}
.wsp-tab .wsp-cnt{font-size:10.5px;font-weight:800;min-width:20px;height:20px;padding:0 6px;
  border-radius:999px;display:inline-flex;align-items:center;justify-content:center;
  background:var(--c-border);color:var(--c-slate);}
.wsp-tab.active .wsp-cnt{background:var(--c-green);color:#fff;}

/* ---- scrollable body (the ONLY scroll region in the panel) ---- */
.wsp-body{flex:1;min-height:0;overflow-y:auto;padding:12px 14px;}

/* ---- entry cards (mockup .wcard) ---- */
.wsp-group{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;
  color:var(--c-muted);margin:6px 2px 8px;}
.wsp-card{display:flex;align-items:center;gap:11px;padding:11px 13px;margin-bottom:8px;
  background:var(--c-surface);border:1px solid var(--c-border);border-radius:13px;
  box-shadow:0 1px 3px rgba(15,23,42,.06);}
.wsp-card.dim{opacity:.6;}
.wsp-time{flex:none;width:52px;text-align:center;font-weight:800;}
.wsp-time-big{font-size:14.5px;letter-spacing:-.2px;color:var(--c-text);}
.wsp-time-big.wait{color:var(--c-amber);}
.wsp-time-sm{display:block;font-size:9.5px;font-weight:700;color:var(--c-muted);
  text-transform:uppercase;letter-spacing:.4px;}
.wsp-mainc{flex:1;min-width:0;}
.wsp-name{display:block;font-size:13.5px;font-weight:800;color:var(--c-text);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wsp-meta{display:flex;align-items:center;gap:8px;font-size:11px;color:var(--c-muted);
  font-weight:600;margin-top:2px;flex-wrap:wrap;}
.wsp-meta svg{color:var(--c-muted);flex:none;}
.wsp-notified{font-size:9px;font-weight:800;letter-spacing:.5px;padding:2px 7px;border-radius:6px;
  color:var(--c-amber);background:var(--c-amberLight);border:1px solid var(--c-amberBorder);
  text-transform:uppercase;}
.wsp-chip{font-size:9px;font-weight:800;letter-spacing:.5px;padding:2px 7px;border-radius:6px;
  color:var(--c-muted);background:var(--c-faint);border:1px solid var(--c-border);
  text-transform:uppercase;white-space:nowrap;}
.wsp-acts{flex:none;display:flex;gap:6px;}
.wsp-btn{min-height:44px;padding:0 13px;border-radius:9px;font-family:inherit;font-size:12px;
  font-weight:800;background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);
  cursor:pointer;transition:border-color .12s, color .12s;}
.wsp-btn:hover{border-color:var(--c-greenBorder);color:var(--c-green);}
.wsp-btn.primary{background:var(--c-green);border-color:var(--c-green);color:#fff;
  box-shadow:0 4px 10px color-mix(in srgb, var(--c-green) 30%, transparent);}
.wsp-btn.primary:hover{background:var(--c-greenDark);color:#fff;}

/* ---- empty states (mockup .empty) ---- */
.wsp-empty{display:flex;flex-direction:column;align-items:center;gap:10px;color:var(--c-muted);
  text-align:center;padding:34px 20px;}
.wsp-empty-ic{width:56px;height:56px;border-radius:18px;background:var(--c-faint);
  border:1px solid var(--c-border);display:flex;align-items:center;justify-content:center;}
.wsp-empty-t{font-size:13px;font-weight:700;color:var(--c-slate);}

/* ---- foot: add button per tab (mockup .panel-foot / .add-btn) ---- */
.wsp-foot{flex:none;padding:10px 14px calc(12px + var(--safe-bottom, 0px));
  border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.wsp-add{width:100%;min-height:46px;border-radius:12px;border:1.5px dashed var(--c-greenBorder);
  background:var(--c-greenLight);color:var(--c-green);font-family:inherit;font-size:13.5px;
  font-weight:800;display:flex;align-items:center;justify-content:center;gap:8px;cursor:pointer;}
.wsp-add:hover{border-style:solid;}

/* ---- scrim + modal (mockup .scrim/.modal) ---- */
.wsp-scrim{position:fixed;inset:0;z-index:1200;background:rgba(9,13,22,.45);
  display:flex;align-items:center;justify-content:center;padding:24px;
  -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);}
.wsp-modal{width:min(460px, calc(100vw - 48px));max-height:calc(100dvh - 80px);
  background:var(--c-surface);border:1px solid var(--c-border);border-radius:20px;
  box-shadow:0 20px 60px rgba(0,0,0,.25);display:flex;flex-direction:column;overflow:hidden;}
.wsp-mhead{flex:none;display:flex;align-items:center;gap:10px;padding:16px 20px 12px;}
.wsp-mtitle{font-size:16.5px;font-weight:800;letter-spacing:-.2px;flex:1;color:var(--c-text);}
.wsp-mx{width:44px;height:44px;border-radius:10px;display:flex;align-items:center;justify-content:center;
  color:var(--c-muted);background:var(--c-faint);border:1px solid var(--c-border);cursor:pointer;flex:none;}
.wsp-mx:hover{color:var(--c-red);background:var(--c-redLight);border-color:var(--c-redBorder);}
.wsp-mbody{padding:2px 20px calc(20px + var(--safe-bottom, 0px));overflow-y:auto;}
.wsp-fld{margin-bottom:12px;}
.wsp-fld label{display:block;font-size:11px;font-weight:700;color:var(--c-muted);
  text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px;}
.wsp-fld input,.wsp-fld textarea{width:100%;box-sizing:border-box;min-height:44px;
  border:1px solid var(--c-border);border-radius:11px;padding:0 13px;background:var(--c-surface);
  color:var(--c-text);font-family:inherit;font-size:13.5px;outline:none;}
.wsp-fld input:focus,.wsp-fld textarea:focus{border-color:var(--c-green);}
.wsp-fld textarea{min-height:64px;padding:9px 13px;resize:none;}
.wsp-fldrow{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.wsp-req{color:var(--c-red);}
.wsp-stepper{display:flex;align-items:center;gap:10px;}
.wsp-stepper button{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;
  justify-content:center;color:var(--c-slate);background:var(--c-faint);border:1px solid var(--c-border);
  cursor:pointer;font-family:inherit;}
.wsp-stepper button:hover{color:var(--c-green);border-color:var(--c-greenBorder);}
.wsp-stepper .q{min-width:32px;text-align:center;font-size:16px;font-weight:800;color:var(--c-text);}
.wsp-submit{width:100%;min-height:50px;border-radius:13px;border:none;background:var(--c-green);
  color:#fff;font-family:inherit;font-size:14.5px;font-weight:800;cursor:pointer;
  box-shadow:0 8px 20px color-mix(in srgb, var(--c-green) 38%, transparent);margin-top:4px;}
.wsp-submit:hover{background:var(--c-greenDark);}
.wsp-submit:disabled{background:var(--c-border);color:var(--c-muted);cursor:not-allowed;box-shadow:none;}
`;

/* Faithful extraction — TableServicePage.js:69-77 (timeAgo). */
const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h${rem}m` : `${hrs}h`;
};

/* Faithful extraction — TableServicePage.js:79-83 (formatTime). */
const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Local YYYY-MM-DD (never toISOString — TableServicePage.js:1311-1315).
const todayLocal = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const UsersIcon = ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    </svg>
);

const PlusIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const CloseIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" />
    </svg>
);

const MinusGlyph = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M5 12h14" />
    </svg>
);

const PlusGlyph = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
    </svg>
);

const HourglassIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 2h12M6 22h12" />
        <path d="M7 2v4a5 5 0 0 0 10 0V2M7 22v-4a5 5 0 0 1 10 0v4" />
    </svg>
);

const CalendarIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
);

const SidePanel = forwardRef(function SidePanel({
    waitlistEntries = [],
    upcomingReservations = [],
    pastReservations = [],
    onSeatWaitlist = () => {},
    onSeatReservation = () => {},
    onNotify = () => {},
    onAddWaitlist = async () => false,
    onAddReservation = async () => false,
    onModalOpenChange,
}, ref) {
    const { t } = useLanguage();

    const [tab, setTab] = useState('waitlist');

    // Add-to-waitlist form — TableServicePage.js:176-179.
    const [showWaitModal, setShowWaitModal] = useState(false);
    const [wlName, setWlName] = useState('');
    const [wlPhone, setWlPhone] = useState('');
    const [wlParty, setWlParty] = useState(2);
    const [wlSubmitting, setWlSubmitting] = useState(false);

    // Add-reservation form — TableServicePage.js:182-189.
    const [showResModal, setShowResModal] = useState(false);
    const [resName, setResName] = useState('');
    const [resPhone, setResPhone] = useState('');
    const [resParty, setResParty] = useState(2);
    const [resDate, setResDate] = useState('');
    const [resTime, setResTime] = useState('');
    const [resNotes, setResNotes] = useState('');
    const [resSubmitting, setResSubmitting] = useState(false);

    // Escape-chain centralization (Task W5, see header note): report
    // open/closed state up and expose an imperative close instead of
    // self-dismissing via an own window keydown listener.
    useEffect(() => {
        if (onModalOpenChange) onModalOpenChange(showWaitModal || showResModal);
    }, [showWaitModal, showResModal, onModalOpenChange]);

    useImperativeHandle(ref, () => ({
        closeModals: () => {
            setShowWaitModal(false);
            setShowResModal(false);
        },
    }), []);

    // Faithful extraction — TableServicePage.js:612-638 form half: guard on
    // name, submitting-lock, close+reset only on success (legacy reset only
    // inside its try branch; the parent's catch shows the error toast).
    const submitWaitlist = async () => {
        if (!wlName.trim() || wlSubmitting) return;
        setWlSubmitting(true);
        const ok = await onAddWaitlist({ name: wlName.trim(), phone: wlPhone.trim(), partySize: wlParty });
        setWlSubmitting(false);
        if (ok) {
            setShowWaitModal(false);
            setWlName('');
            setWlPhone('');
            setWlParty(2);
        }
    };

    // Faithful extraction — TableServicePage.js:640-670 form half (guards on
    // name + date + time, close+reset only on success).
    const submitReservation = async () => {
        if (!resName.trim() || !resDate || !resTime || resSubmitting) return;
        setResSubmitting(true);
        const ok = await onAddReservation({
            name: resName.trim(), phone: resPhone.trim(),
            date: resDate, time: resTime, partySize: resParty, notes: resNotes.trim(),
        });
        setResSubmitting(false);
        if (ok) {
            setShowResModal(false);
            setResName('');
            setResPhone('');
            setResParty(2);
            setResDate('');
            setResTime('');
            setResNotes('');
        }
    };

    // TableServicePage.js:1310-1316 — opening the reservation modal presets
    // the date to today (local) and clears the time.
    const openResModal = () => {
        setResDate(todayLocal());
        setResTime('');
        setShowResModal(true);
    };

    const guestName = (rec) => rec.guest?.name || rec.guest_name || t('guest');

    const renderWaitlist = () => (
        waitlistEntries.length === 0 ? (
            <div className="wsp-empty" role="status">
                <div className="wsp-empty-ic">{HourglassIcon}</div>
                <div className="wsp-empty-t">{t('no_guests_waiting')}</div>
            </div>
        ) : (
            waitlistEntries.map((entry) => (
                <div className="wsp-card" key={entry.id}>
                    <span className="wsp-time">
                        <span className="wsp-time-big wait">{timeAgo(entry.created_at || entry.added_at)}</span>
                        <span className="wsp-time-sm">{t('waiting')}</span>
                    </span>
                    <span className="wsp-mainc">
                        <span className="wsp-name">{guestName(entry)}</span>
                        <span className="wsp-meta">
                            <UsersIcon /> {entry.party_size}
                            {entry.guest?.phone && <span>{entry.guest.phone}</span>}
                            {entry.status === 'notified' && <span className="wsp-notified">{t('notified')}</span>}
                        </span>
                    </span>
                    <span className="wsp-acts">
                        <button type="button" className="wsp-btn" data-act="notify" onClick={() => onNotify(entry)}>
                            {t('notify')}
                        </button>
                        {['waiting', 'notified'].includes(entry.status) && (
                            <button type="button" className="wsp-btn primary" data-act="seat-waitlist" onClick={() => onSeatWaitlist(entry)}>
                                {t('seat')}
                            </button>
                        )}
                    </span>
                </div>
            ))
        )
    );

    const renderReservations = () => (
        upcomingReservations.length === 0 && pastReservations.length === 0 ? (
            <div className="wsp-empty" role="status">
                <div className="wsp-empty-ic">{CalendarIcon}</div>
                <div className="wsp-empty-t">{t('no_reservations_today')}</div>
            </div>
        ) : (
            <>
                {upcomingReservations.length > 0 && (
                    <>
                        <div className="wsp-group">{t('upcoming')}</div>
                        {upcomingReservations.map((res) => (
                            <div className="wsp-card" key={res.id}>
                                <span className="wsp-time">
                                    <span className="wsp-time-big">{formatTime(res.scheduled_for)}</span>
                                    <span className="wsp-time-sm">{t('today')}</span>
                                </span>
                                <span className="wsp-mainc">
                                    <span className="wsp-name">{guestName(res)}</span>
                                    <span className="wsp-meta">
                                        <UsersIcon /> {res.party_size}
                                        {res.table_number && <span>{t('table')} {res.table_number}</span>}
                                        {res.notes && <span>{res.notes}</span>}
                                    </span>
                                </span>
                                {['booked', 'confirmed'].includes(res.status) && (
                                    <span className="wsp-acts">
                                        <button type="button" className="wsp-btn primary" data-act="seat-reservation" onClick={() => onSeatReservation(res)}>
                                            {t('seat')}
                                        </button>
                                    </span>
                                )}
                            </div>
                        ))}
                    </>
                )}
                {pastReservations.length > 0 && (
                    <>
                        <div className="wsp-group">{t('past')}</div>
                        {pastReservations.map((res) => (
                            <div className="wsp-card dim" key={res.id}>
                                <span className="wsp-time">
                                    <span className="wsp-time-big">{formatTime(res.scheduled_for)}</span>
                                    <span className="wsp-time-sm">{t('today')}</span>
                                </span>
                                <span className="wsp-mainc">
                                    <span className="wsp-name">{guestName(res)}</span>
                                    <span className="wsp-meta">
                                        <UsersIcon /> {res.party_size}
                                        <span className="wsp-chip">
                                            {t(res.status)}
                                        </span>
                                    </span>
                                </span>
                            </div>
                        ))}
                    </>
                )}
            </>
        )
    );

    return (
        <div className="wsp-root">
            <style>{CSS}</style>

            {/* tabs + counts (waitlist total; reservations = upcoming only, legacy 1184) */}
            <div className="wsp-tabs" role="tablist" aria-label={t('guest_lists')}>
                <button
                    type="button" role="tab" aria-selected={tab === 'waitlist'}
                    className={`wsp-tab${tab === 'waitlist' ? ' active' : ''}`}
                    onClick={() => setTab('waitlist')}
                >
                    {t('waitlist')}
                    {waitlistEntries.length > 0 && <span className="wsp-cnt">{waitlistEntries.length}</span>}
                </button>
                <button
                    type="button" role="tab" aria-selected={tab === 'reservations'}
                    className={`wsp-tab${tab === 'reservations' ? ' active' : ''}`}
                    onClick={() => setTab('reservations')}
                >
                    {t('reservations')}
                    {upcomingReservations.length > 0 && <span className="wsp-cnt">{upcomingReservations.length}</span>}
                </button>
            </div>

            <div className="wsp-body" role="tabpanel" aria-label={tab === 'waitlist' ? t('waitlist') : t('reservations')}>
                {tab === 'waitlist' ? renderWaitlist() : renderReservations()}
            </div>

            <div className="wsp-foot">
                {tab === 'waitlist' ? (
                    <button type="button" className="wsp-add" data-act="open-add-waitlist" onClick={() => setShowWaitModal(true)}>
                        {PlusIcon}
                        {t('add_to_waitlist')}
                    </button>
                ) : (
                    <button type="button" className="wsp-add" data-act="open-add-reservation" onClick={openResModal}>
                        {PlusIcon}
                        {t('add_reservation')}
                    </button>
                )}
            </div>

            {/* ---- Add to Waitlist modal (legacy 1453-1598) ---- */}
            {showWaitModal && createPortal(
                <div
                    className="wsp-scrim"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowWaitModal(false); }}
                >
                    <div className="wsp-modal" role="dialog" aria-modal="true" aria-label={t('add_to_waitlist')}>
                        <div className="wsp-mhead">
                            <div className="wsp-mtitle">{t('add_to_waitlist')}</div>
                            <button type="button" className="wsp-mx" aria-label={t('close')} onClick={() => setShowWaitModal(false)}>
                                {CloseIcon}
                            </button>
                        </div>
                        <div className="wsp-mbody">
                            <div className="wsp-fld">
                                <label>{t('guest_name')}</label>
                                <input
                                    type="text" value={wlName} autoFocus
                                    placeholder={t('enter_guest_name')}
                                    onChange={(e) => setWlName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && wlName.trim()) submitWaitlist(); }}
                                />
                            </div>
                            <div className="wsp-fld">
                                <label>{t('phone')}</label>
                                <input
                                    type="tel" inputMode="tel" value={wlPhone}
                                    placeholder={t('enter_contact_number')}
                                    onChange={(e) => setWlPhone(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && wlName.trim()) submitWaitlist(); }}
                                />
                            </div>
                            <div className="wsp-fld">
                                <label>{t('party_size')}</label>
                                <div className="wsp-stepper">
                                    <button type="button" aria-label={t('decrease_party_size')} onClick={() => setWlParty(Math.max(1, wlParty - 1))}>
                                        {MinusGlyph}
                                    </button>
                                    <span className="q">{wlParty}</span>
                                    <button type="button" aria-label={t('increase_party_size')} onClick={() => setWlParty(wlParty + 1)}>
                                        {PlusGlyph}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="button" className="wsp-submit"
                                disabled={!wlName.trim() || wlSubmitting}
                                onClick={submitWaitlist}
                            >
                                {wlSubmitting ? '…' : t('add_to_waitlist')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ---- Add Reservation modal (legacy 1600-1787) ---- */}
            {showResModal && createPortal(
                <div
                    className="wsp-scrim"
                    onClick={(e) => { if (e.target === e.currentTarget) setShowResModal(false); }}
                >
                    <div className="wsp-modal" role="dialog" aria-modal="true" aria-label={t('add_reservation')}>
                        <div className="wsp-mhead">
                            <div className="wsp-mtitle">{t('add_reservation')}</div>
                            <button type="button" className="wsp-mx" aria-label={t('close')} onClick={() => setShowResModal(false)}>
                                {CloseIcon}
                            </button>
                        </div>
                        <div className="wsp-mbody">
                            <div className="wsp-fld">
                                <label>{t('guest_name')} <span className="wsp-req">*</span></label>
                                <input
                                    type="text" value={resName} autoFocus
                                    placeholder={t('enter_guest_name')}
                                    onChange={(e) => setResName(e.target.value)}
                                />
                            </div>
                            <div className="wsp-fld">
                                <label>{t('phone')}</label>
                                <input
                                    type="tel" inputMode="tel" value={resPhone}
                                    placeholder={t('enter_contact_number')}
                                    onChange={(e) => setResPhone(e.target.value)}
                                />
                            </div>
                            <div className="wsp-fldrow">
                                <div className="wsp-fld">
                                    <label>{t('date')} <span className="wsp-req">*</span></label>
                                    <input type="date" value={resDate} onChange={(e) => setResDate(e.target.value)} />
                                </div>
                                <div className="wsp-fld">
                                    <label>{t('time')} <span className="wsp-req">*</span></label>
                                    <input type="time" value={resTime} onChange={(e) => setResTime(e.target.value)} />
                                </div>
                            </div>
                            <div className="wsp-fld">
                                <label>{t('party_size')}</label>
                                <div className="wsp-stepper">
                                    <button type="button" aria-label={t('decrease_party_size')} onClick={() => setResParty(Math.max(1, resParty - 1))}>
                                        {MinusGlyph}
                                    </button>
                                    <span className="q">{resParty}</span>
                                    <button type="button" aria-label={t('increase_party_size')} onClick={() => setResParty(resParty + 1)}>
                                        {PlusGlyph}
                                    </button>
                                </div>
                            </div>
                            <div className="wsp-fld">
                                <label>{t('notes')}</label>
                                <input
                                    type="text" value={resNotes}
                                    placeholder={t('special_requests')}
                                    onChange={(e) => setResNotes(e.target.value)}
                                />
                            </div>
                            <button
                                type="button" className="wsp-submit"
                                disabled={!resName.trim() || !resDate || !resTime || resSubmitting}
                                onClick={submitReservation}
                            >
                                {resSubmitting ? '…' : t('add_reservation')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
});

export default SidePanel;
